import { PropsWithChildren, useEffect } from "react";
import { useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import { loginSelector } from "../store/features/loginSlice";
import { UserTypes } from "../types/user.types";
import PermissionDeniedPage from "../common/ui/page/PermissionDeniedPage";
import Loader from "../common/ui/Loader";
import { DefaultErrorBoundary } from "@/common/ui/error/Error";

type ProtectedRouteGuardProps = PropsWithChildren & {
  allowedUserTypes: UserTypes;
  notRoles?: string[];
  permId: string | string[];
  perms: any;
};

export default function ProtectedRouteGuard({ allowedUserTypes, permId, perms, children, notRoles }: ProtectedRouteGuardProps) {
  const { currentUser } = useSelector(loginSelector);
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) navigate("/login");
  }, [currentUser, navigate]);

  if (currentUser === undefined) {
    return <Loader />;
  }

  if (currentUser === null) {
    return <Navigate to="/login" replace />;
  }
  if (notRoles && notRoles.includes(currentUser.role)) {
    return <PermissionDeniedPage />;
  }
  const hasAllowedUserType = allowedUserTypes?.includes("ALL") || allowedUserTypes.includes(currentUser.userType);
  const allPermissions = perms.flatMap((p: any) => p.permissionIds);
  const hasPermission =
    permId === "all" || (Array.isArray(permId) ? allPermissions.some((id: any) => permId.includes(id)) : allPermissions.includes(permId || ""));
  const isUserRolePermissionCheck =
    (notRoles && notRoles.includes(currentUser.role)) || (notRoles && notRoles.includes("User") && Boolean(currentUser.isCloneUserRole));

  if (!hasAllowedUserType || !hasPermission || isUserRolePermissionCheck) {
    return <PermissionDeniedPage />;
  }

  return <DefaultErrorBoundary>{children}</DefaultErrorBoundary>;
}
